/**
 * @aicc/inference — provider adapters. All providers speak an OpenAI-compatible
 * chat/completions shape over a pluggable transport (so the network layer can be the
 * webview fetch, the Tauri HTTP plugin, or a fake in tests).
 *   - "openai-compat": BYO (LMStudio, LlamaCPP, OpenRouter, NanoGPT-direct, any OpenAI API).
 *   - "website-proxy": PRIMARY. Subscription-gated passthrough on aicc-node (Phase 4);
 *      carries the app JWT instead of an API key.
 *   - "rin-sub": the Rin Chat subscription on api.rin.chat. Same shape: a session token from `getAppJwt`,
 *      no API key, and the model is a SLOT name (basic / balanced / smart) the server resolves.
 */
import { errorFrame, InferenceError, parseSSE, type StreamChunk, type ToolCall } from "./sse";

/**
 * The network call, injectable — the "pluggable transport" this module's header has always claimed.
 *
 * It exists for ONE reason: a remote client has no API key (the desktop refuses to send credentials to a
 * browser), so its provider requests have to be relayed through the desktop, which holds the key and
 * attaches it. Everything above this line stays identical in both environments — the same prompt
 * assembly, the same SSE parsing, the same abort handling.
 */
export type HttpFetch = (url: string, init: RequestInit) => Promise<Response>;

const directFetch: HttpFetch = (url, init) => fetch(url, init);
let httpFetch: HttpFetch = directFetch;

/** Swap the transport. `null` restores the direct one. */
export function setInferenceFetch(f: HttpFetch | null): void {
  httpFetch = f ?? directFetch;
}

export type { StreamChunk, ToolCall };
export type ProviderKind = "website-proxy" | "openai-compat" | "rin-sub";
/** The kinds that authenticate with a session token from `getAppJwt` instead of an API key. */
const tokenAuthKind = (k: ProviderKind): boolean => k === "website-proxy" || k === "rin-sub";

export interface ProviderConfig {
  id: string;
  kind: ProviderKind;
  label: string;
  baseUrl: string; // up to /v1, e.g. http://localhost:1234/v1
  apiKey?: string;
  model: string;
  /** Optional second model on THIS SAME provider to auto-retry with when `model` is overloaded (429/5xx).
   *  Empty/undefined = no fallback. See withFallback() in the app's providers lib. */
  fallbackModel?: string;
  /** Reasoning behavior for THIS provider/model — how much to think, how to ask for it on the wire, and
   *  which markers its thinking arrives in. Lives here because all three differ per provider and model.
   *  Absent on configs saved before this existed; callers fall back to defaults. */
  reasoning?: ProviderReasoning;
  /** Whether this endpoint supports OpenAI function/tool calling. Off by default; when on, the app may send
   *  `tools` (model-triggerable generators) and run tool round-trips. Provider-specific, so opt-in per config. */
  supportsTools?: boolean;
  /** How many requests this provider may run AT ONCE. Unset or 0 = unlimited, which is what a hosted API
   *  wants. A local model on one GPU is the opposite case: parallel calls split the same tokens/second, so
   *  a reply crawls while a summary runs beside it. Enforced app-side in lib/providerLimit, which queues
   *  rather than failing; the inference layer itself never counts its own calls. */
  maxConcurrent?: number;
  /** Seconds this endpoint may send nothing before a request is given up as stalled: the wait for the first
   *  token, and any gap mid-reply. Unset = 120; 0 = never. A local model reading a long prompt sends no
   *  bytes until it has read all of it, which on a slow GPU can take longer than a hosted API ever would. */
  responseTimeoutSec?: number;
  /** Hard ceiling on the context window for THIS endpoint/model, in tokens. Sampler presets are shared
   *  across providers, so a preset sized for a 128k model would overflow a 16k one; this clamps the
   *  preset's `maxContextTokens` down at prompt-assembly time. Auto-filled from the endpoint's `/models`
   *  when it reports a context length, else set by hand. Unset/0 = no cap (the preset stands). */
  maxContextTokens?: number;
}

/** Per-provider reasoning config. See ProviderConfig.reasoning. */
export interface ProviderReasoning {
  /** Parse thinking out of replies into a collapsible block (and strip it from what's sent back). */
  enabled: boolean;
  /** Opening / closing markers this model's thinking arrives in, e.g. <think> … </think>. Also the
   *  envelope native `reasoning_content` is folded into. */
  prefix: string;
  suffix: string;
  /** "auto" omits the effort field entirely (provider default). */
  effort: "auto" | ReasoningEffort;
  format: ReasoningFormat;
  /** Compute reasoning but don't return it. */
  exclude: boolean;
  /** Also send `chat_template_kwargs: { enable_thinking }` — see SamplerSettings.reasoningTemplateFlag. */
  templateFlag?: boolean;
  /** In-prompt "don't think" tag for GLM (`/nothink`) / Qwen3 (`/no_think`) — see SamplerSettings.noThinkTag. */
  noThinkTag?: string;
}

/** An OpenAI-style function tool definition, sent in CompletionRequest.tools. */
export interface ToolDef {
  type: "function";
  function: { name: string; description?: string; parameters?: Record<string, unknown> };
}

export interface ChatMessage {
  role: "system" | "user" | "assistant" | "tool";
  content: string;
  /** Image attachments (data: URIs). Sent as OpenAI-style multimodal content parts; a provider that
   *  doesn't support vision rejects the request with its own error rather than silently ignoring them. */
  images?: string[];
  /** Optional participant name (OpenAI multi-speaker field). Set in group chats to attribute each
   *  turn to its character. Must match ^[a-zA-Z0-9_-]{1,64}$ — sanitize before setting. */
  name?: string;
  /** On an assistant message: the tool calls the model made (echoed back so the follow-up round has context). */
  tool_calls?: ToolCall[];
  /** On a role:"tool" message: which tool call this is the result for. */
  tool_call_id?: string;
}

/**
 * How hard the model should "think" before answering. Maps to the OpenAI-compatible `reasoning_effort`
 * field (OpenAI, Gemini, xAI, Groq, OpenRouter, NanoGPT, …). Omitted entirely when unset ("auto"), so
 * the provider's own default applies. `none` asks models that support it to skip thinking altogether —
 * useful when a model dumps its chain-of-thought into the reply and burns the token budget.
 */
export type ReasoningEffort = "none" | "minimal" | "low" | "medium" | "high" | "xhigh";

/**
 * How to put the effort on the wire. Gateways disagree, so this is user-selectable when the default
 * doesn't suit a provider:
 *  - "effort": top-level `reasoning_effort` (OpenAI, NanoGPT, Gemini, xAI, Groq) — the widest default.
 *  - "nested": `reasoning: { effort, exclude }` (OpenRouter, NanoGPT).
 *  - "both": send both, for gateways that only read one and ignore the other.
 *  - "thinking": explicit `thinking: { type: "enabled" | "disabled" }` toggle (official DeepSeek, Anthropic).
 *    "none" → `{type:"disabled"}` with NO reasoning_effort (these 400 on "none"); a real tier → enabled + tier.
 *  - "off": send nothing — for strict providers that 400 on an unknown field.
 */
export type ReasoningFormat = "auto" | "effort" | "nested" | "both" | "thinking" | "off";

/**
 * The wire shape a host is known to take, for "auto".
 *
 * The same division of labour the tier narrowing below already uses: the APP knows the dialect, so the
 * user does not have to. Asking someone which field name their gateway reads is asking them to debug our
 * request for us, and the honest answer for most people is "whichever one stopped the 400".
 *
 * CURRENTLY EMPTY, on purpose, and that is the finding rather than an omission.
 *
 * Every provider this app ships a preset for speaks OpenAI-compatible `/v1/chat/completions` (NanoGPT,
 * OpenRouter, OpenAI, xAI, DeepSeek, Ollama, KoboldCpp, TextGen, LM Studio, plus a custom URL), and all of
 * them read the flat `reasoning_effort`. The two that refuse it do so only at the `none` tier, which
 * `effectiveReasoningFormat` already rewrites per host, and `exclude` is carried on the nested object even
 * in flat mode, so OpenRouter needs nothing either.
 *
 * The first draft of this listed Anthropic, which was wrong in a way worth recording: Anthropic's native
 * API is `/v1/messages`, a different request shape entirely, so it is not reachable from here at all and
 * no reasoning field could have saved it. Claude is reached through a gateway, where the flat field is
 * correct. A table entry for an endpoint the app cannot talk to is not a fix, it is a claim.
 *
 * So `auto` resolves to `effort` today, and it is still the right default: it stops the UI asking the user
 * which field name their gateway reads, which is a question about OUR request. The bar for the first real
 * entry is a request that FAILS without it, not a shape that looks tidier.
 */
function reasoningFormatForHost(_host: string): ReasoningFormat {
  return "effort";
}

/**
 * The wire format to actually use, given what was asked for and which endpoint it's going to.
 *
 * "none" is the problem tier. Gateways (NanoGPT, OpenRouter) read it as "don't reason", but several
 * NATIVE APIs reject it outright — and they don't agree on what to do instead:
 *
 *  - DeepSeek takes low/high/max and turns thinking off via `thinking:{type:"disabled"}`.
 *  - xAI (Grok) 400s with "This model does not support `reasoning_effort` value `none`" and has no
 *    thinking toggle at all, so the only way to say "don't reason" is to say nothing.
 *
 * Routed by host because the app sends "none" on its own behalf all over the place — writing an image
 * prompt, suggesting a title, picking a speaker — where the user never chose an effort and shouldn't
 * have to know their provider's dialect to make those features work.
 *
 * Only ever applies to "none". A real tier is passed through untouched, and an explicit "off" is
 * respected as the user overriding all of this.
 */
export function effectiveReasoningFormat(
  requested: ReasoningFormat | undefined,
  effort: ReasoningEffort | undefined,
  baseUrl: string,
): ReasoningFormat {
  const host = (baseUrl || "").toLowerCase();
  // "auto" (and an unset value) asks the app to pick. An explicit choice is always honored, including a
  // wrong one: someone who went looking for this setting is overriding us on purpose.
  const fmt = !requested || requested === "auto" ? reasoningFormatForHost(host) : requested;
  if (effort !== "none" || fmt === "off") return fmt;
  if (host.includes("api.deepseek.com")) return "thinking";
  if (host.includes("api.x.ai")) return "off";
  return fmt;
}

/**
 * The tier to actually send, for endpoints that accept a narrower set than the app offers.
 *
 * The app's menu spans none/minimal/low/medium/high/xhigh because that's the union across gateways. A
 * native API that implements three of them rejects the rest with a hard 400 rather than clamping, so the
 * nearest supported tier is substituted here — the user asked for "as little thinking as possible", not
 * for the literal string "minimal", and failing the request serves neither.
 *
 * xAI (docs.x.ai/developers/model-capabilities/text/reasoning) documents low/medium/high/xhigh and
 * defaults to high. "none" never reaches this: effectiveReasoningFormat has already turned that into
 * "send nothing", which is how you get xAI's default rather than a 400, and grok-4.5 states reasoning
 * cannot be disabled at all, so there is no honest way to express "none" there. Only `minimal` is still
 * narrowed, to `low`, because it is not one of xAI's levels.
 *
 * `xhigh` is NOT narrowed on any xAI model any more. It used to be folded to "high" everywhere except
 * grok-4.20-multi-agent, where it selects the agent count (4 or 16) rather than depth and folding would
 * have quietly halved the agents. That fold is gone: xAI now lists xhigh as an ordinary level on
 * grok-4.6+ and downgrades it themselves on models that lack it, so the fold had stopped protecting
 * anything and was costing the tier a user picked.
 *
 * Only narrows what a provider is known to refuse. Anything unrecognised is passed through untouched,
 * because guessing a provider's vocabulary is how you break the ones that were working.
 */
export function effectiveReasoningEffort(
  effort: ReasoningEffort | undefined,
  baseUrl: string,
  model?: string,
): ReasoningEffort | undefined {
  if (!effort) return effort;
  const m = (model || "").toLowerCase();
  // OpenAI's levels are MODEL-dependent, and the app sends "none" on its own behalf all over the place:
  // a chat title, an image prompt, a speaker pick. On a model that has no "none" that is a hard 400 on a
  // feature the user never associated with reasoning, which is the same shape of bug xAI's "none" was.
  // Per developers.openai.com/api/docs/models: GPT-6.1 Sol and GPT-6 Astra both support exactly low,
  // medium, high, xhigh and max, so NEITHER takes "none" or "minimal", and "low" is the cheapest either
  // will accept. GPT-6 Sol DOES take "none" and is deliberately not here. Omitting the field is the wrong
  // fallback: that gets the model's default, which is medium, and a chat title is not worth medium.
  if (/api\.openai\.com|\.openai\.azure\.com/.test((baseUrl || "").toLowerCase())) {
    if (/gpt-6\.1-sol|gpt-6-astra/.test(m) && (effort === "none" || effort === "minimal")) return "low";
  }
  if ((baseUrl || "").toLowerCase().includes("api.x.ai")) {
    // "minimal" is still not one of xAI's levels, so it becomes the nearest thing that is.
    if (effort === "minimal") return "low";
    // xhigh is NOT folded any more. It was, back when xAI documented low/medium/high only and the one
    // model that took xhigh was grok-4.20-multi-agent. The docs now list it as a normal level on
    // grok-4.6 and later, and say a model that lacks it treats the request as "high" rather than
    // refusing it. So the fold had stopped protecting anything and started costing the tier: a user
    // picking Maximum on grok-4.6 got "high" and no way to tell. Passing it through is both correct on
    // the new models and safe on the old ones, by xAI's own rule.
  }
  return effort;
}

/**
 * Markers used to fold provider-native reasoning (streamed in its own field) into the message text, so
 * the rest of the app sees ONE representation regardless of how a gateway delivers thinking. Defaults
 * match the app's reasoning settings; call setReasoningMarkers to keep them in sync with the user's.
 */
let reasoningMarkers = { prefix: "<think>", suffix: "</think>" };
export function setReasoningMarkers(prefix: string, suffix: string): void {
  if (prefix && suffix) reasoningMarkers = { prefix, suffix };
}

/**
 * The in-prompt "don't think" tag a model family reads, for a caller that hasn't set one.
 *
 * These models ignore `reasoning_effort` entirely — whether they think is a chat-template flag — so
 * choosing "None — don't think" against GLM or Qwen3 used to send a field the model never reads and the
 * thinking appeared anyway, with nothing in the UI suggesting which two other switches had to be found
 * and set by hand. The tag differs by an underscore between the two families, which is why this is
 * derived from the model id rather than being one constant.
 *
 * An explicit `noThinkTag` still wins — this only fills the blank.
 */
export function defaultNoThinkTag(model: string | undefined): string {
  const m = model ?? "";
  if (/qwen/i.test(m)) return "/no_think";
  if (/glm/i.test(m)) return "/nothink";
  return "";
}

// Operation label for the NEXT exchange(s). Stamped onto each exchange when it's CREATED (see makeProvider),
// so a concurrent chat changing it can't relabel an already-started exchange. Set it right before streaming.
let exchangeContext = "chat";
export function setExchangeContext(label: string): void {
  exchangeContext = label;
}

/** Sampler / decoding knobs. All optional; omitted fields are left off the request body. */
export interface SamplerSettings {
  temperature?: number;
  maxTokens?: number;
  reasoningEffort?: ReasoningEffort;
  /** Wire format for the effort. Unset or "auto" lets the app pick from the endpoint
   *  (`reasoningFormatForHost`), which today means top-level `reasoning_effort` for everyone. */
  reasoningFormat?: ReasoningFormat;
  /** Ask the provider to compute reasoning but omit it from the response (`reasoning.exclude`). */
  reasoningExclude?: boolean;
  /**
   * Also send `chat_template_kwargs: { enable_thinking: effort !== "none" }`.
   *
   * GLM-4.5+ and Qwen3 (and most vLLM / SGLang deployments of them) do NOT read `reasoning_effort` at
   * all — whether they think is a chat-template flag baked into the prompt. Sending `reasoning_effort:
   * "none"` to those is silently ignored and the model thinks anyway. This is a separate switch rather
   * than another `ReasoningFormat` because one gateway (NanoGPT, OpenRouter) fronts BOTH kinds of model,
   * so it has to compose with whatever format the provider's other models need.
   */
  reasoningTemplateFlag?: boolean;
  /** Merge adjacent system messages for THIS request — the shape-rejection retry's internal flag
   *  (callers use ProviderOptions.mergeSystemsFor instead). */
  mergeSystems?: boolean;
  /** Drop `seed` from THIS request — the seed-rejection retry's internal flag (callers use
   *  ProviderOptions.seedRejectedFor instead). */
  dropSeed?: boolean;
  /** Drop `stream_options` from THIS request: the usage-rejection retry's internal flag (callers use
   *  ProviderOptions.streamUsageRejectedFor instead). */
  dropStreamUsage?: boolean;
  /**
   * Soft switch appended to the last USER message when effort is "none" — GLM wants `/nothink`, Qwen3
   * wants `/no_think` (yes, they differ by an underscore, which is why this is free text rather than a
   * checkbox). These models read the tag out of the prompt itself, so it works even through gateways
   * that drop unknown body fields like `chat_template_kwargs`. Ignored at any other effort, and only sent
   * to a model whose id names one of those families (glm / qwen) — any other model reads it as plain text.
   */
  noThinkTag?: string;
  topP?: number;
  topK?: number;
  minP?: number;
  topA?: number;
  typicalP?: number;
  tfs?: number;
  smoothingFactor?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;
  repetitionPenalty?: number;
  seed?: number;
  /** Stop sequences — generation halts when any is produced. */
  stop?: string[];
  // DRY repetition penalty (llama.cpp / text-gen-webui / KoboldCpp).
  dryMultiplier?: number;
  dryBase?: number;
  dryAllowedLength?: number;
  drySequenceBreakers?: string[];
  // XTC (exclude top choices).
  xtcThreshold?: number;
  xtcProbability?: number;
  // Mirostat.
  mirostatMode?: number;
  mirostatTau?: number;
  mirostatEta?: number;
  // CFG (classifier-free guidance) — supported by some local backends (KoboldCpp, text-gen-webui,
  // TabbyAPI). Ignored by backends that don't understand them.
  cfgScale?: number;
  negativePrompt?: string;
}

/**
 * One block the app spliced INTO the chat history at a depth (an Author's Note, a lorebook entry at a
 * position, a generator event, a vector recall), named so the prompt inspector can say what it is.
 * `index` is the message's position in the array that goes on the wire; `depth` is how many messages from
 * the end it was placed. Built by @aicc/chat-engine (AssembledPrompt.injections) and carried here so a
 * CAPTURED exchange can be labeled too, not just the preview.
 */
export interface PromptInjection {
  index: number;
  label: string;
  depth: number;
}

export interface CompletionRequest extends SamplerSettings {
  messages: ChatMessage[];
  /** Provenance for the depth-spliced messages in `messages`, for the debug inspector only. Never sent to
   *  the provider: it is copied onto the RawExchange, and remapped if the wire shape merges messages. */
  injections?: PromptInjection[];
  model?: string;
  signal?: AbortSignal;
  /** OpenAI function tools to expose to the model. Passed through verbatim; omitted when empty. */
  tools?: ToolDef[];
  /** OpenAI tool_choice ("auto" | "none" | "required" | {…}). Omitted when unset. */
  tool_choice?: unknown;
  /** OpenAI `response_format` — e.g. `{ type: "json_object" }`. Constrains decoding so the reply is
   *  guaranteed-parseable JSON on providers that implement it. Omitted when unset; see the note at the
   *  send site, since not every gateway accepts it. */
  response_format?: unknown;
  /** Tags the recorded exchange so the inspector can scope to one chat (see RawExchange.channel). */
  channel?: string;
  /** Operation label for the debug inspector ("chat"/"regenerate"/…). Passed per-request so concurrent
   *  chats can't mislabel each other; falls back to the module global when absent (utility calls). */
  context?: string;
  /** Extra request headers, added after the provider's own (the subscription's X-Rin-Purpose). */
  headers?: Record<string, string>;
}

export interface TransportArgs {
  url: string;
  headers: Record<string, string>;
  body: unknown;
  signal?: AbortSignal;
  /** Give up after this long with no data, in ms; 0 = never. See `idleTimeoutMs`. */
  idleMs?: number;
}
export type Transport = (args: TransportArgs) => AsyncIterable<string | Uint8Array>;

export interface Provider {
  readonly config: ProviderConfig;
  stream(req: CompletionRequest): AsyncIterable<StreamChunk>;
  /** Non-streaming completion — one request/response, returns the full assistant text. For background /
   *  utility calls (title, summary, scene, restructure) where streaming buys nothing and a mid-stream
   *  failure can't be cleanly surfaced/retried. Same request shape; sends `stream: false`. */
  complete(req: CompletionRequest): Promise<string>;
}

/**
 * One request/response, captured verbatim for the in-app debug inspector — the request `body` is the
 * object serialized to the wire, `headers` are as sent (including auth), and `response` is the raw
 * stream text exactly as received. No redaction.
 */
export interface RawExchange {
  seq: number; // monotonic order within the current log
  /** Which chat this exchange belongs to (CompletionRequest.channel) — lets the inspector scope to one
   *  chat even when several chats stream concurrently. Undefined for utility calls (title/scene/etc.). */
  channel?: string;
  /** Operation label ("chat"/"regenerate"/"summary"/…), captured when the request STARTS so concurrent
   *  streams can't mislabel each other (see setExchangeContext). */
  context?: string;
  url: string;
  method: "POST";
  headers: Record<string, string>;
  body: unknown;
  response: string; // raw response stream, exactly as received (SSE text, error body, etc.)
  assistantText: string; // the assistant content parsed out of the stream
  /** What each depth-spliced message in `body.messages` is, by index (see PromptInjection). */
  injections?: PromptInjection[];
  error?: string;
  done: boolean;
}

// Rolling log of exchanges. The caller (a chat view) clears it when switching chats so it scopes to
// the current chat; a soft cap guards against unbounded growth in a very long session.
const EXCHANGE_CAP = 500;
let exchanges: RawExchange[] = [];
let exchangeSeq = 0;
/** Captured exchanges (oldest first). Pass a `channel` (chat id) to get only that chat's exchanges —
 *  concurrent chats each stream into the one log, so the inspector filters by channel. */
export function getExchanges(channel?: string): RawExchange[] {
  return channel === undefined ? exchanges : exchanges.filter((e) => e.channel === channel);
}
/** Drop the log — all of it, or only one channel's (chat's) entries. */
export function clearExchanges(channel?: string): void {
  if (channel === undefined) { exchanges = []; exchangeSeq = 0; }
  else exchanges = exchanges.filter((e) => e.channel !== channel);
}

// Global sink: unlike `exchanges` (per-chat, cleared on switch), registered sinks receive EVERY
// completed exchange — chat, regenerate, continue, impersonate, summary, test connection — so a
// persistent debug log can capture them all. The exchange passed is the redacted copy (no API keys).
export type ExchangeSink = (ex: RawExchange) => void;
let exchangeSinks: ExchangeSink[] = [];
/** Register a listener for every completed exchange. Returns an unsubscribe fn. */
export function onExchangeComplete(fn: ExchangeSink): () => void {
  exchangeSinks.push(fn);
  return () => { exchangeSinks = exchangeSinks.filter((f) => f !== fn); };
}
function notifyExchangeSinks(ex: RawExchange): void {
  for (const fn of exchangeSinks) { try { fn(ex); } catch { /* a bad sink must not break generation */ } }
}

// Mask API keys in the RECORDED exchange so a shared debug log can't leak them. The real request
// still sends the true values — only the inspector copy is redacted.
const SECRET_HEADER_RE = /^(authorization|x-api-?key|api-?key)$/i;
function redactHeaders(h: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(h)) {
    out[k] = SECRET_HEADER_RE.test(k) ? v.replace(/(Bearer\s+)?\S[\s\S]*/i, (_m, scheme: string | undefined) => `${scheme ?? ""}***redacted***`) : v;
  }
  return out;
}
function redactUrlSecrets(url: string): string {
  return url.replace(/([?&](?:api[_-]?key|key|access_token|token)=)[^&]+/gi, "$1***redacted***");
}

/**
 * Start recording one exchange for the debug inspector, keeping only the most recent `EXCHANGE_CAP`.
 *
 * Both transports (`stream` and `complete`) begin here rather than each building the record, because of the
 * REDACTION: the recorded copy has its secrets masked while the real request goes out with the true headers,
 * and a debug log the user shares must not carry their API key. One place to redact is one place to get that
 * right.
 *
 * The returned record is filled in as the exchange proceeds and handed to `notifyExchangeSinks` when it
 * settles.
 */
function beginExchange(
  req: { channel?: string; context?: string; injections?: PromptInjection[] },
  url: string,
  headers: Record<string, string>,
  body: unknown,
): RawExchange {
  const exchange: RawExchange = {
    seq: ++exchangeSeq,
    channel: req.channel,
    context: req.context ?? exchangeContext, // prefer the per-request label; fall back to the global for utility calls
    injections: req.injections,
    url: redactUrlSecrets(url),
    method: "POST",
    headers: redactHeaders(headers),
    body,
    response: "",
    assistantText: "",
    done: false,
  };
  exchanges.push(exchange);
  if (exchanges.length > EXCHANGE_CAP) exchanges.shift();
  return exchange;
}

export interface ProviderOptions {
  transport?: Transport;
  getAppJwt?: () => Promise<string>;
  /**
   * What this model actually accepts, learned from an earlier rejection — see `onEffortRejected`.
   * Returning a tier substitutes it for what the caller asked; "off" sends no reasoning fields at all.
   * Consulted per request, so a fix learned on one turn applies to the next without a restart.
   */
  effortFor?: (model: string, requested: ReasoningEffort | undefined) => ReasoningEffort | "off" | undefined;
  /** Called after a rejection is worked around, with what will be used instead. The app persists it (so
   *  the next request costs one call, not two) and tells the user what changed. */
  onEffortRejected?: (info: { model: string; requested?: ReasoningEffort; using: ReasoningEffort | "off"; supported: string[] }) => void;
  /** Does this model need adjacent system messages merged into one? Learned from a shape rejection
   *  (see rejectedSystemPlacement) — most providers take the multi-message shape and keep it. */
  mergeSystemsFor?: (model: string) => boolean;
  /** A model just refused the multi-system shape and the retry merged — the app persists the answer. */
  onSystemsRejected?: (model: string) => void;
  /** Does this endpoint+model refuse a `seed`? Learned from a seed rejection (see rejectedSeedField) — most
   *  providers accept or ignore it, so a seed is sent by default and only withheld from routes that 400. */
  seedRejectedFor?: (model: string) => boolean;
  /** A model just refused a `seed` and the retry dropped it — the app persists the answer per endpoint+model. */
  onSeedRejected?: (model: string) => void;
  /** Does this endpoint+model refuse `stream_options`? Learned the same way as a seed refusal. */
  streamUsageRejectedFor?: (model: string) => boolean;
  /** A model just refused `stream_options` and the retry dropped it: the app persists it per endpoint+model. */
  onStreamUsageRejected?: (model: string) => void;
  /** Rewrites what the debug inspector RECORDS of the response and of an error (never what the caller gets).
   *  The Rin Chat subscription uses it to keep the real model id out of view: its server passes upstream
   *  frames through byte for byte, and those name the model. */
  redactRecorded?: (text: string, kind: "response" | "error") => string;
  /** The least total time a NON-streamed call may take before the app gives up, in ms. Without it the cap is
   *  the provider's Response timeout counted from the start. The Rin Chat subscription keeps a long call
   *  alive (200 headers, then a space every 15 s) and only then sends the body, so a legitimate long call
   *  needs the server's own upper bound here instead. A Response timeout of 0 (never) still wins. */
  completeTimeoutMs?: number;
}

function joinUrl(base: string, path: string): string {
  return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

/** Pull the human-readable message out of a provider's error body ({"error":{"message":...}}, {"message":...},
 *  or a bare string), trimmed short. Empty if there's nothing useful. */
function upstreamDetail(body: string): string {
  if (!body) return "";
  // Every caller ends its sentence after the detail, so the detail must not end one itself: a provider
  // that punctuates ("…use the 'lms load' command.") came out as "command..". Cut AFTER the length cap,
  // or a cap landing mid-sentence would put the stray stop back.
  const bare = (s: string) => s.replace(/[\s.。!?]+$/u, "");
  try {
    const j = JSON.parse(body) as { error?: { message?: unknown } | string; message?: unknown };
    const m = (typeof j.error === "object" ? j.error?.message : j.error) ?? j.message;
    if (typeof m === "string" && m.trim()) return bare(m.trim().replace(/\s+/g, " ").slice(0, 200));
  } catch { /* not JSON — fall through */ }
  return bare(body.replace(/\s+/g, " ").trim().slice(0, 160));
}

/** Seconds to wait before retrying, from a Retry-After header or a "retry-after=NN" hint in the body. */
function retryAfterSeconds(body: string, header?: string | null): number | null {
  if (header && /^\d+$/.test(header.trim())) return parseInt(header.trim(), 10);
  const m = body.match(/retry[-\s]?after[=:\s]+(\d+)/i);
  return m ? parseInt(m[1], 10) : null;
}

// InferenceError lives in sse.ts (a 200 error frame is one too, and sse.ts cannot import this file).
export { InferenceError, errorFrame, StreamCutError } from "./sse";

/**
 * Did the endpoint reject the request specifically because of the reasoning/thinking fields?
 *
 * Every model disagrees about which tiers exist, and the app sends `reasoning_effort: "none"` on its own
 * behalf all over the place — writing an image prompt, naming a chat, Rin's card edits — where the user
 * never chose an effort. A model that only accepts, say, high/max then 400s ("Invalid value for
 * reasoning_effort ... Supported values are: high, max"), and the feature looks broken while ordinary chat
 * on the same model works fine, because chat sends whatever the user picked (usually nothing).
 *
 * There's no list to consult: it varies per model and changes without notice, so the only reliable signal
 * is the rejection itself. `stream`/`complete` retry once with the fields dropped.
 */
/** Did the endpoint's chat template refuse the message SHAPE — a system message that isn't first?
 *  Matched on the error text alone: the failure can arrive as an HTTP error OR as the stream's first
 *  SSE error event (LM Studio surfaces the Jinja raise that way), so there is no reliable status. */
/** A thrown value as text, for the rejection MATCHERS below. Every one of them reads the provider message:
 *  the failure can arrive as an HTTP error, a plain string, or an SSE error event, and the wording is the
 *  only reliable signal. (The app side has its own `errText` in lib/coerce; a package cannot reach it.) */
const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e));
/** First line of a mid-history system message folded into a user turn, for a template that only takes a system
 *  message first (see mergeSystems in buildRequest). */
export const SYSTEM_NOTE_LABEL = "[Note, not said by the user]";

export function rejectedSystemPlacement(e: unknown): boolean {
  const msg = errText(e);
  return /system message must (?:be|come)[\s\S]{0,40}?(?:beginning|first)|multiple system messages|only one system message/i.test(msg);
}

export function rejectedReasoningField(e: unknown): boolean {
  return (
    e instanceof InferenceError &&
    (e.status === 400 || e.status === 422) &&
    /reasoning_effort|reasoning\b|thinking/i.test(e.message)
  );
}

/** A 400/422 whose message blames `seed` — a strict endpoint that doesn't accept the field. Scoped to
 *  require BOTH a mention of seed AND a rejection word, so a 400 that merely happens to contain "seed"
 *  (e.g. a moderation snippet) doesn't get the seed dropped out from under an unrelated failure. Takes
 *  the bare error TEXT too, like rejectedStreamUsage, so the chat's failure notice can recognize the
 *  handled first attempt from the string the exchange records. */
export function rejectedSeedField(e: unknown): boolean {
  if (e instanceof InferenceError ? e.status !== 400 && e.status !== 422 : typeof e !== "string") return false;
  const text = errText(e);
  return /\bseed\b/i.test(text) &&
    /unsupported|unrecognized|unknown|unexpected|not supported|not allowed|invalid|no such|extra fields?|additional propert/i.test(text);
}

/** A rejection that names `stream_options` / `include_usage`: an endpoint that does not take the field.
 *  Takes the bare error TEXT too (not only an InferenceError), because the recorded exchange keeps the
 *  error as a string and the chat's failure notice has to recognize the handled first attempt. */
export function rejectedStreamUsage(e: unknown): boolean {
  if (e instanceof InferenceError && e.status !== 400 && e.status !== 422) return false;
  return /stream_options|include_usage/i.test(errText(e));
}

/** Cheapest first, for RANKING a rejection's supported tiers against what was asked. The tier is sent
 *  VERBATIM, so this list is about distance, not legality, and it is deliberately LONGER than the menu:
 *  "max" is a real tier on DeepSeek and GLM that this app never offers, yet a model can name it in a
 *  rejection, and an unlisted tier ranks -1, which reads as maximally distant from everything. That put
 *  "max" out of reach of the nearest-tier walk unless it was the only option: asked for xhigh and told
 *  "supported: low, max", the walk scored low at 3 and max at 6 and picked LOW, the opposite end from
 *  the request. Anything still unlisted keeps the old -1 behavior, which is the right default for a
 *  spelling we know nothing about. */
const EFFORT_ORDER = ["none", "minimal", "low", "medium", "high", "xhigh", "max"];

/**
 * How to retry a request the model refused on reasoning grounds: which tier to adopt, and the request
 * that carries it. Pure, so the two rules below can be tested without a server.
 *
 * `using` is "off" when there is nothing to adopt, and ALSO when the request asked for no thinking in the
 * first place. That second case is not an optimization: the retry used to adopt the model's first
 * supported tier, so a 300-token title or image-prompt call spent its whole budget thinking and came back
 * empty. A request that wanted no thinking must never retry with thinking.
 *
 * Going off clears every reasoning field rather than just the effort. Leaving a format or an exclude flag
 * behind re-sends part of the shape that was just refused, which is the same 400 again.
 */
export function reasoningRetryPlan(req: CompletionRequest, errorText: string): {
  using: ReasoningEffort | "off";
  supported: string[];
  req: CompletionRequest;
} {
  const supported = supportedEfforts(errorText);
  const tier = nearestSupportedEffort(req.reasoningEffort, supported);
  const using = req.reasoningEffort === "none" ? "off" : ((tier as ReasoningEffort | undefined) ?? "off");
  return {
    using,
    supported,
    req: using === "off"
      ? { ...req, reasoningEffort: undefined, reasoningFormat: "off", reasoningExclude: false, reasoningTemplateFlag: false }
      : { ...req, reasoningEffort: using },
  };
}

/**
 * Of the tiers a model says it takes, the one NEAREST to what was asked rather than the cheapest: a
 * rejected "xhigh" on a model listing low/medium/high belongs at high, not low. Empty `supported` gives
 * undefined, and the caller then sends no reasoning fields at all.
 *
 * `requested` is always one of ours, so an unrankable one means the app asked for no tier; it takes the
 * cheapest on offer, since that branch is a utility call that wanted none.
 */
export function nearestSupportedEffort(requested: string | undefined, supported: string[]): string | undefined {
  const wantRank = EFFORT_ORDER.indexOf(requested ?? "");
  if (wantRank < 0) return supported[0];
  return [...supported].sort((a, b) =>
    Math.abs(effortRank(a) - wantRank) - Math.abs(effortRank(b) - wantRank))[0];
}

/**
 * Where a tier sits in EFFORT_ORDER, with a spelling we do not know ranked LAST rather than at -1.
 *
 * Both callers must agree on this or they contradict each other: a tier sorted last by the lister was
 * simultaneously treated as beyond-cheapest by the nearest-tier walk, which raw `indexOf` ranks at -1,
 * one step below "none". An unknown tier volunteered alongside known ones is far likelier to be exotic
 * and expensive ("max", "ultra") than cheaper than not thinking at all.
 */
function effortRank(s: string): number {
  const i = EFFORT_ORDER.indexOf(s);
  return i < 0 ? EFFORT_ORDER.length : i;
}

/**
 * The tiers a model listed in its own rejection ("Supported values are: high, max"), cheapest first.
 *
 * Models that refuse `"none"` almost always say what they DO take, which is a better answer than dropping
 * reasoning entirely: the request still succeeds, and it succeeds the way the model expects. Empty when
 * the message doesn't name any.
 */
export function supportedEfforts(message: string): string[] {
  const m = /supported values? (?:are|is)[:\s]+([^.\n]+)/i.exec(message);
  if (!m) return [];
  const found = m[1]
    .split(/[,/]|\bor\b|\band\b/i)
    .map((s) => s.trim().replace(/^[`'"]|[`'"]$/g, "").toLowerCase())
    .filter((s) => /^[a-z_-]{2,12}$/.test(s));
  return [...new Set(found)].sort((a, b) => effortRank(a) - effortRank(b));
}

/** Provider-side overload / transient outage — the cases worth auto-retrying on (429 + 5xx). */
export function isOverloadStatus(status: number): boolean {
  return status === 429 || status === 500 || status === 502 || status === 503 || status === 504;
}

/** Turn an upstream HTTP failure into a clean, actionable message for end users (no raw JSON for the common
 *  transient cases). 429/5xx are provider-side overload/outages → suggest waiting or switching models. */
/** The AICC proxy's "daily budget spent" reply (the contest prize API: a fixed number of tokens per UTC day):
 *  `{ error: "Daily limit reached", message, resetsAt }`. Its `message` is written for people already, so it
 *  is shown as-is. Keyed on `error`, not the status: every other 429 is still an overload. */
export function dailyLimitMessage(body: string): string | null {
  try {
    const j = JSON.parse(body) as { error?: unknown; message?: unknown };
    return j?.error === "Daily limit reached" && typeof j.message === "string" && j.message.trim() ? j.message.trim() : null;
  } catch {
    return null;
  }
}

/** The stable `code` and `resetsAt` an error body carries, in either shape the proxies send:
 *  `{ error: { code } }` (the AICC proxy) or a top-level `{ code, resetsAt }` (api.rin.chat). */
export function errorBodyCode(body: string): { code: string | null; resetsAt: string | null; requestId: string | null } {
  try {
    type Fields = { code?: unknown; resetsAt?: unknown; requestId?: unknown };
    const j = JSON.parse(body) as Fields & { error?: Fields | unknown };
    const inner = j?.error && typeof j.error === "object" ? (j.error as Fields) : {};
    const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
    return {
      code: str(inner.code) ?? str(j?.code),
      resetsAt: str(inner.resetsAt) ?? str(j?.resetsAt),
      requestId: str(inner.requestId) ?? str(j?.requestId),
    };
  } catch {
    return { code: null, resetsAt: null, requestId: null };
  }
}

/** An InferenceError for an HTTP failure body, with everything the body says on it. */
export function inferenceErrorFor(status: number, body: string, retryAfter: string | null): InferenceError {
  const { code, resetsAt, requestId } = errorBodyCode(body);
  // The header first, then the body's `retryAfterSec` (api.rin.chat and the AICC proxy send both forms).
  let wait = retryAfterSeconds(body, retryAfter);
  if (wait === null) {
    try {
      const j = JSON.parse(body) as { retryAfterSec?: unknown; error?: { retryAfterSec?: unknown } };
      const v = j?.retryAfterSec ?? j?.error?.retryAfterSec;
      if (typeof v === "number" && Number.isFinite(v) && v >= 0) wait = v;
    } catch { /* not JSON */ }
  }
  return new InferenceError(status, humanInferenceError(status, body, retryAfter), wait, !!dailyLimitMessage(body), code, resetsAt, requestId);
}

export function humanInferenceError(status: number, body: string, retryAfter?: string | null): string {
  const secs = retryAfterSeconds(body, retryAfter);
  const wait = secs ? `try again in about ${secs}s` : "try again in a minute";
  if (status === 429) {
    const daily = dailyLimitMessage(body);
    if (daily) return daily;
    // OpenAI (and its clones) answer an out-of-credit key with 429 + insufficient_quota — telling the
    // user to wait is exactly wrong there.
    if (/insufficient_quota|exceeded your current quota|quota|billing|credit/i.test(body)) {
      return "Your provider account is out of credit or over its quota (HTTP 429) — top up or check billing with the provider; waiting won't help.";
    }
    return `This model is temporarily overloaded — too many people are using it right now. Please ${wait}, or switch to another model or provider for now.`;
  }
  if (status === 502 || status === 503 || status === 504 || status === 500) {
    return `The model provider is having a temporary problem (HTTP ${status}). Please ${wait}, or switch to another model or provider for now.`;
  }
  const detail = upstreamDetail(body);
  if (status === 401 || status === 403) {
    return `The provider rejected the request (HTTP ${status})${detail ? `: ${detail}` : ""}. Check the provider's API key in Settings → Providers.`;
  }
  if (status === 402) {
    return `The provider reports the account is out of credit or over its limit (HTTP 402)${detail ? `: ${detail}` : ""}.`;
  }
  if (status === 404) {
    return `The model or endpoint wasn't found (HTTP 404)${detail ? `: ${detail}` : ""}. Check the model name and base URL in Settings → Providers.`;
  }
  return `Inference failed (HTTP ${status})${detail ? `: ${detail}` : ""}.`;
}

/** No bytes from the server for this long → treat the stream as stalled and abort (covers time-to-first
 *  byte and mid-stream stalls). Reset on every chunk, so a model that's actively streaming never trips it. */
const IDLE_TIMEOUT_MS = 120_000;

/** The stall limit for one provider, in ms: its Response timeout when set (0 = never), else the default. */
export function idleTimeoutMs(config: Pick<ProviderConfig, "responseTimeoutSec">): number {
  const s = config.responseTimeoutSec;
  return typeof s === "number" && Number.isFinite(s) && s >= 0 ? Math.round(s * 1000) : IDLE_TIMEOUT_MS;
}

/** SSE keep-alive only — comment lines (": ping") and blank lines. A proxy that queues a request sends these
 *  for minutes, and they used to reset the idle clock, so a request that never got an answer never timed
 *  out either: five minutes of "Almost there…" with Stop as the only way out. A chunk split mid-line counts
 *  as data (conservative — the clock is only withheld from pure keep-alive). */
const keepAliveDecoder = new TextDecoder();
export function isKeepAliveChunk(chunk: Uint8Array | string): boolean {
  const text = typeof chunk === "string" ? chunk : keepAliveDecoder.decode(chunk, { stream: true });
  return text.split(/\r?\n/).every((line) => line.trim() === "" || line.startsWith(":"));
}

/** Default transport: webview/Node fetch streaming the raw response body. Guards against a provider that
 *  connects then goes silent — without this the caller's "generating" state hangs forever. */
const fetchTransport: Transport = async function* ({ url, headers, body, signal, idleMs = IDLE_TIMEOUT_MS }) {
  const idle = new AbortController();
  let stalled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const arm = () => {
    if (timer) clearTimeout(timer);
    if (!idleMs) return; // this provider's Response timeout is 0: never give up
    timer = setTimeout(() => {
      stalled = true;
      idle.abort();
    }, idleMs);
  };
  // Fold the caller's signal (Stop / chat-switch / delete) into ours so it still aborts the fetch.
  const onAbort = () => idle.abort();
  if (signal) {
    if (signal.aborted) idle.abort();
    else signal.addEventListener("abort", onAbort, { once: true });
  }
  arm();
  try {
    const res = await httpFetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: idle.signal,
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => "");
      throw inferenceErrorFor(res.status, detail, res.headers.get("retry-after"));
    }
    if (!res.body) throw new Error("Inference response has no body to stream.");
    const reader = res.body.getReader();
    let finished = false;
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) { finished = true; break; }
        if (value && !isKeepAliveChunk(value)) arm(); // got data → reset the idle clock; keep-alive pings do not count
        if (value) yield value;
      }
    } finally {
      // A consumer that stops early (breaks out of the stream without aborting) must not leave the
      // HTTP body streaming into a buffer nobody reads — cancel tears the connection down.
      if (finished) reader.releaseLock();
      else reader.cancel().catch(() => {});
    }
  } catch (e) {
    // A stall is OUR abort, not the user's — surface it as a real, actionable error (a plain Error, not an
    // AbortError, so the UI shows it instead of silently treating it as a cancel).
    if (stalled && !(signal && signal.aborted)) {
      throw new Error(
        `The model stopped responding (no data for ${idleMs / 1000}s). Check the provider/model and try again, or raise the provider's Response timeout in Settings if a long prompt is just slow to read.`,
      );
    }
    throw e;
  } finally {
    if (timer) clearTimeout(timer);
    if (signal) signal.removeEventListener("abort", onAbort);
  }
};

/** OpenRouter's app attribution: without these its activity log lists every request as an "Unknown" app.
 *  `X-OpenRouter-Title` is the current name for the older `X-Title`. Only to OpenRouter: a custom header makes
 *  the browser preflight, which a local server may refuse. */
export function appAttributionHeaders(url: string): Record<string, string> {
  return /^https?:\/\/([a-z0-9-]+\.)*openrouter\.ai(\/|$)/i.test(url)
    ? { "HTTP-Referer": "https://rin.chat", "X-OpenRouter-Title": "Rin Chat" }
    : {};
}

export function makeProvider(config: ProviderConfig, opts: ProviderOptions = {}): Provider {
  const transport = opts.transport ?? fetchTransport;
  // Build the wire request (url + headers + body) shared by streaming and non-streaming calls. `streaming`
  // only toggles the `stream` field; auth, the model gate, the no-think tag, and every sampler / reasoning /
  // tools / CFG field are identical either way.
  const buildRequest = async (req: CompletionRequest, streaming: boolean): Promise<{ url: string; headers: Record<string, string>; body: Record<string, unknown>; injections?: PromptInjection[] }> => {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (tokenAuthKind(config.kind)) {
        if (!opts.getAppJwt) throw new Error(`${config.kind} provider requires getAppJwt.`);
        headers.Authorization = `Bearer ${await opts.getAppJwt()}`;
      } else if (config.apiKey) {
        headers.Authorization = `Bearer ${config.apiKey}`;
      }
      if (req.headers) Object.assign(headers, req.headers);
      // A provider can be SAVED with no model; only reject it at use-time, with a clear message instead of
      // a raw server 400. (The website proxy selects the model server-side, so it's exempt.)
      const model = req.model ?? config.model;
      if (!model && !tokenAuthKind(config.kind)) {
        throw new Error("No model selected for this provider — pick a model in Settings before sending.");
      }
      // OpenAI-standard fields plus common local-server extensions (top_k, min_p,
      // repetition_penalty). Undefined fields are omitted so servers use their own defaults;
      // servers that don't recognize an extension ignore it.
      // Appended to the LAST USER turn, not the literal last message: on a continue/prefill the final
      // message is the assistant's, and a stray "/nothink" inside the model's own words would be echoed
      // back as text. Non-mutating — history belongs to the caller.
      // [code]…[/code] marks text as inert (no macros, no generators — see card-runtime/code.ts). The
      // markers survive prompt assembly so each pass keeps honouring them, and come off HERE, the last
      // point before the wire: the model gets the literal contents and never the markers themselves.
      // Inlined rather than importing card-runtime — one regex isn't worth a package dependency.
      // Guard on "[", not "[code]": the strip is case-INsensitive, so a literal check would let a
      // `[CODE]` through — same reason card-runtime's protectCode tests for the bracket.
      let outMessages: ChatMessage[] = req.messages.map((m) =>
        m.content.includes("[") ? { ...m, content: m.content.replace(/\[\/?code\]/gi, "") } : m,
      );
      // Only models that READ a soft switch get one. To any other model the tag is plain text in the user's
      // turn: a tester's `/think:off` on DeepSeek was echoed back verbatim ("/think:off[Tiffany speaks…]") and
      // pushed the model into inline reasoning — the opposite of what the field is for.
      // ponytail: family match on the model id; a renamed local GLM/Qwen finetune won't get the tag. Add a
      // per-config override if someone reports thinking leaking on one with effort at None.
      // Note it reads req.reasoningEffort, the RAW request, not the learned `wanted` further down. That
      // is what keeps "none" working on a model whose reasoning FIELD has been learned-refused: `wanted`
      // is undefined there, which drops the field (right) and the chat-template flag (also right, since
      // rejectedReasoningField matches "thinking", so the refusal may have named the flag itself). The
      // tag is the one switch that cannot be what a gateway refused, because it is only message text, so
      // it is also the only one left. Three GLM models in real use are in exactly that state.
      const tag = req.noThinkTag?.trim() || defaultNoThinkTag(model);
      if (tag && req.reasoningEffort === "none" && /glm|qwen/i.test(model ?? "")) {
        const at = outMessages.map((m) => m.role).lastIndexOf("user");
        if (at !== -1 && !outMessages[at].content.trimEnd().endsWith(tag)) {
          outMessages = outMessages.map((m, i) =>
            i === at ? { ...m, content: `${m.content.trimEnd()} ${tag}` } : m,
          );
        }
      }
      // ADJACENT system messages merge into one — but ONLY for models that have rejected the multi-
      // message shape (learned via mergeSystemsFor, or this request's own retry). The app builds the
      // opening as several system turns (character prompt, persona, quality directives); most providers
      // accept that and it stays untouched for them, but some local chat templates raise for any system
      // message that isn't first (Qwen3-family: "System message must be at the beginning") and 500 every
      // character chat. Joining a run with blank lines is semantically identical; messages carrying
      // images never merge (their content becomes parts below — no template accepts system images anyway).
      // Injection labels are positional, so merging has to move them with the messages: the leading system
      // block collapses to one message here, which shifts every depth injection after it.
      let injections = req.injections;
      if (req.mergeSystems || opts.mergeSystemsFor?.(model ?? "")) {
        const wireIndex: number[] = [];
        outMessages = outMessages.reduce<ChatMessage[]>((acc, m, i) => {
          const prev = acc[acc.length - 1];
          if (prev && prev.role === "system" && m.role === "system" && !prev.images?.length && !m.images?.length) {
            acc[acc.length - 1] = { ...prev, content: `${prev.content}\n\n${m.content}` };
          } else {
            acc.push(m);
          }
          wireIndex[i] = acc.length - 1;
          return acc;
        }, []);
        // A system message after the conversation has started (an Author's Note or a lorebook entry at depth)
        // is still not first, and the same templates raise for it: a merged retry kept failing on a Qwen model
        // in LM Studio. On this path only, it folds into the next user turn, else the one before, labelled so
        // the model doesn't read it as the user speaking. Every other model keeps its separate system turns.
        const firstChat = outMessages.findIndex((m) => m.role !== "system");
        if (firstChat !== -1 && outMessages.some((m, j) => j > firstChat && m.role === "system")) {
          const folded: ChatMessage[] = [];
          const at: number[] = [];
          let carry = "";
          let carried: number[] = [];
          outMessages.forEach((m, j) => {
            if (j > firstChat && m.role === "system" && !m.images?.length) {
              const note = `${SYSTEM_NOTE_LABEL}\n${m.content}`;
              const prev = folded[folded.length - 1];
              if (outMessages[j + 1]?.role === "user") { carry += `${note}\n\n`; carried.push(j); return; }
              if (prev?.role === "user") folded[folded.length - 1] = { ...prev, content: `${prev.content}\n\n${note}` };
              else folded.push({ ...m, role: "user", content: note });
              at[j] = folded.length - 1;
              return;
            }
            folded.push(carry && m.role === "user" ? { ...m, content: carry + m.content } : m);
            at[j] = folded.length - 1;
            if (carry && m.role === "user") { for (const k of carried) at[k] = at[j]; carry = ""; carried = []; }
          });
          outMessages = folded;
          for (let i = 0; i < wireIndex.length; i++) if (wireIndex[i] !== undefined) wireIndex[i] = at[wireIndex[i]];
        }
        injections = injections?.map((inj) => ({ ...inj, index: wireIndex[inj.index] ?? inj.index }));
      }
      // Vision: a message carrying images goes out as OpenAI content PARTS (text + image_url entries);
      // text-only messages keep the plain-string shape, which every server accepts. The mapping happens
      // here at the wire — everything upstream (trimming, token estimates, nothink tags) works on the
      // plain string content.
      const wireMessages = outMessages.map((m) => {
        if (!m.images?.length) return m;
        const { images, ...rest } = m;
        return {
          ...rest,
          content: [
            ...(m.content ? [{ type: "text", text: m.content }] : []),
            ...images.map((url) => ({ type: "image_url", image_url: { url } })),
          ],
        };
      });
      const body: Record<string, unknown> = {
        model,
        messages: wireMessages,
        stream: streaming,
      };
      const set = (k: string, v: unknown) => {
        if (v !== undefined && v !== null && v !== "") body[k] = v;
      };
      set("temperature", req.temperature);
      set("max_tokens", req.maxTokens);
      set("top_p", req.topP);
      set("top_k", req.topK);
      set("min_p", req.minP);
      set("top_a", req.topA);
      set("typical_p", req.typicalP);
      set("tfs", req.tfs);
      set("smoothing_factor", req.smoothingFactor);
      set("frequency_penalty", req.frequencyPenalty);
      set("presence_penalty", req.presencePenalty);
      set("repetition_penalty", req.repetitionPenalty);
      set("dry_multiplier", req.dryMultiplier);
      set("dry_base", req.dryBase);
      set("dry_allowed_length", req.dryAllowedLength);
      if (req.drySequenceBreakers && req.drySequenceBreakers.length) body.dry_sequence_breakers = req.drySequenceBreakers;
      set("xtc_threshold", req.xtcThreshold);
      set("xtc_probability", req.xtcProbability);
      set("mirostat_mode", req.mirostatMode);
      set("mirostat_tau", req.mirostatTau);
      set("mirostat_eta", req.mirostatEta);
      // Seed is withheld from a route that has 400'd on it before (learned via seedRejectedFor) or on this
      // request's own drop-and-retry — otherwise sent, so most providers get a fresh seed each generation.
      if (!req.dropSeed && !opts.seedRejectedFor?.(model ?? "")) set("seed", req.seed);
      // Reasoning effort — only sent when explicitly chosen, so the default request shape is unchanged.
      // Providers that don't know the field ignore it; a strict one may reject a tier it doesn't
      // implement, which is why the format is user-selectable (including "off").
      // A tier this model has already rejected once is not sent again — see ProviderOptions.effortFor.
      // Applied before everything below, so the learned answer wins over both the request and the host rules.
      const learned = opts.effortFor?.(model, req.reasoningEffort);
      // A learned tier proves only that the endpoint 400s on `reasoning_effort: "none"` — a caller asking
      // for silence still wants silence. So a "none" request keeps every soft off-switch (enable_thinking,
      // /nothink, thinking:disabled) and merely stops sending the rejected FIELD (noneKept, honored in the
      // branches below); the learned tier substitutes only when the caller wanted real thinking. Without
      // this, the fix itself switched thinking ON for utility calls, whose tight max_tokens then came
      // back as finish_reason "length" with empty content — every token spent inside the reasoning.
      const noneKept = req.reasoningEffort === "none" && learned !== undefined && learned !== "off";
      const wanted = learned === "off" ? undefined : noneKept ? "none" : learned ?? req.reasoningEffort;
      const rFmt = learned === "off" ? "off" : effectiveReasoningFormat(req.reasoningFormat, wanted, config.baseUrl);
      // Narrowed once, here, so every branch below sends a tier the endpoint actually implements. Takes
      // the model too, because on xAI one tier is valid for exactly one model.
      const rEffort = effectiveReasoningEffort(wanted, config.baseUrl, model);
      // Insurance for the same case: a gateway may not forward the soft switches to the model, which then
      // thinks inside a budget sized for the answer alone. Headroom is cheap; an empty reply isn't.
      if (req.maxTokens && noneKept) {
        body.max_tokens = req.maxTokens + 1024;
      }
      if (rFmt === "thinking") {
        // Official DeepSeek / Anthropic explicit toggle — the fix for APIs that 400 on reasoning_effort:"none".
        // Only when the user chose an effort. "none" → engine OFF via {type:"disabled"} and NO reasoning_effort;
        // a real tier → {type:"enabled"} plus reasoning_effort carrying the tier.
        if (rEffort) {
          const on = rEffort !== "none";
          body.thinking = { type: on ? "enabled" : "disabled" };
          if (on) body.reasoning_effort = rEffort;
        }
      } else if (rFmt !== "off" && (rEffort || req.reasoningExclude)) {
        // effort/nested/both send the tier VERBATIM, INCLUDING "none" — gateways like NanoGPT / OpenRouter read
        // "none" to disable reasoning. A native API that 400s on "none" (DeepSeek) should use "thinking" instead.
        if (rEffort && !noneKept && (rFmt === "effort" || rFmt === "both")) {
          body.reasoning_effort = rEffort;
        }
        if (rFmt === "nested" || rFmt === "both") {
          const nested: Record<string, unknown> = {};
          if (rEffort && !noneKept) nested.effort = rEffort;
          if (req.reasoningExclude) nested.exclude = true;
          body.reasoning = nested;
        } else if (req.reasoningExclude) {
          // "exclude" only exists on the nested object, so carry it there even in top-level mode.
          body.reasoning = { exclude: true };
        }
      }
      // Independent of the format above (and of "off"), because it's the ONLY switch some models read.
      // `reasoning_effort` is not a field these families read at all, so without this the user's chosen
      // effort is ignored outright: asking GLM or Qwen3 for NO thinking left them thinking anyway and the
      // block "randomly popped up". A gateway that rejects the field still retries without it (see
      // rejectedReasoningField).
      //
      // The list is one of MECHANISM, which families decide by chat template, NOT one of defaults, and
      // that distinction is the only reason it is safe to keep. Defaults split inside a single family and
      // flip between releases (Granite 3.2 defaults thinking off, 4.2 defaults it on), so a defaults table
      // would need the version and would rot the way the xhigh fold did. Mechanism does not move.
      //
      // The AUTOMATIC path stays "none"-only, and that is a deliberate limit rather than an oversight.
      // `chat_template_kwargs` is the documented mechanism on a direct backend (vLLM, SGLang, llama.cpp)
      // but it is undocumented on the gateways most of these models are actually reached through:
      // NanoGPT and OpenRouter both document a full `reasoning_effort` enum (none/minimal/low/medium/
      // high/xhigh) and normalize thinking themselves, so the kwarg buys nothing there and only adds
      // surface for a 400. That 400 is unusually expensive here: rejectedReasoningField matches
      // "thinking", so a refusal naming the kwarg records a learned "off" and takes reasoning out
      // entirely for that model. Sending it unasked on every request is not worth that.
      //
      // "none" is the exception because it is the case the path was built for: a family that defaults
      // thinking ON ignores `reasoning_effort: none`, so without the flag (or the in-prompt tag) there is
      // no way to say "stop". Turning thinking ON needs no rescue, since any non-none effort already
      // reaches a gateway that honors it.
      //
      // A user on a direct backend whose model defaults thinking OFF (Gemma 4, Granite 3.2) still gets
      // the ON direction from the explicit switch, which is what that switch is for and which already
      // sent `true` before any of this. So the limit costs nothing that was previously available.
      const templateDecides = /glm|qwen|granite|gemma/i.test(model ?? "");
      const autoNoThink = wanted === "none" && templateDecides;
      if ((req.reasoningTemplateFlag || autoNoThink) && wanted && learned !== "off") {
        // BOTH spellings, because the KEY is per family, not just the tag: Qwen3, GLM, Gemma 4 and
        // Granite 4.2 read `enable_thinking`, while DeepSeek V3.1 and Granite 3.2 read `thinking`. A
        // chat template simply does not reference the key it doesn't use, so the spare one is inert,
        // and sending both replaces a model table that would rot the way the xhigh fold did. One wrong
        // key is not a 400, it is the flag being silently ignored and the thinking appearing anyway,
        // which is the bug this whole path exists to fix.
        const on = wanted !== "none";
        body.chat_template_kwargs = { enable_thinking: on, thinking: on };
      }
      if (req.stop && req.stop.length) body.stop = req.stop;
      // CFG: only send when guidance is actually engaged (scale > 1), with its negative prompt.
      if (req.cfgScale !== undefined && req.cfgScale > 1) {
        body.guidance_scale = req.cfgScale;
        set("negative_prompt", req.negativePrompt);
      }
      // Function tools — only when there are any (empty array omitted). Providers without tool support that
      // ignore unknown fields are unaffected; strict ones is why this is gated behind a per-provider opt-in.
      if (req.tools && req.tools.length) {
        body.tools = req.tools;
        if (req.tool_choice !== undefined && req.tool_choice !== null) body.tool_choice = req.tool_choice;
      }
      // Structured output (`{"type":"json_object"}` / `{"type":"json_schema",…}`). Passed through verbatim
      // and only when set — support is uneven across OpenAI-compatible gateways, and a strict one 400s on
      // a field it doesn't know, so the caller owns the decision (and any fallback).
      if (req.response_format) body.response_format = req.response_format;
      // Ask for the token count on a stream. Without it most OpenAI-compatible servers report no usage
      // when streaming, so every count was an estimate. The answer is one extra frame with `choices: []`
      // before [DONE], which parseSSE passes over and the recorded response keeps. Both ProviderKinds speak
      // OpenAI chat/completions; a strict endpoint that refuses the field is retried once without it and
      // remembered (streamUsageRejectedFor), like a refused seed.
      if (streaming && !req.dropStreamUsage && !opts.streamUsageRejectedFor?.(model ?? "")) {
        body.stream_options = { include_usage: true };
      }
      const url = joinUrl(config.baseUrl, "chat/completions");
      Object.assign(headers, appAttributionHeaders(url));
      return { url, headers, body, injections };
  };

  /**
   * Rebuild a request the model rejected over its reasoning fields — the auto-fix.
   *
   * Preference order: a tier the model itself named in the rejection (cheapest one, so a model demanding
   * high/max costs high, not max), otherwise no reasoning fields at all. The tier is used VERBATIM, since
   * the model's spelling is the one it validates against ("max" is not a tier this app has).
   *
   * `onEffortRejected` hands the answer to the app, which remembers it per model and says so — the point
   * being that the SECOND request never pays for this discovery, and the user learns their "don't think"
   * setting isn't available on this model rather than silently getting thinking they didn't ask for.
   */
  const reasoningFallback = async (req: CompletionRequest, err: unknown, streaming: boolean) => {
    const plan = reasoningRetryPlan(req, errText(err));
    opts.onEffortRejected?.({
      model: req.model ?? config.model ?? "",
      requested: req.reasoningEffort,
      using: plan.using,
      supported: plan.supported,
    });
    return buildRequest(plan.req, streaming);
  };

  /** One retry with adjacent system messages merged, when the endpoint's chat template rejects the
   *  message SHAPE (see rejectedSystemPlacement). Only when nothing has reached the caller yet — this
   *  failure arrives before any content (often as the stream's first SSE error event). The rejection is
   *  reported via onSystemsRejected so the app remembers it per model and the next request merges up
   *  front. NOT applied by default: most providers accept (and some prompts rely on) multiple system
   *  messages, so the shape only changes for models that have refused it. */
  const streamWithShapeRetry = async function* (self: { stream(r: CompletionRequest): AsyncIterable<StreamChunk> }, req: CompletionRequest): AsyncIterable<StreamChunk> {
    let yielded = false;
    try {
      for await (const c of self.stream(req)) { yielded = true; yield c; }
    } catch (e) {
      const model = req.model ?? config.model ?? "";
      // Seed refused — drop it and retry once, remembering the route per (endpoint, model). Checked before
      // the system-shape retry; both only fire when nothing has reached the caller yet.
      if (!yielded && !req.dropSeed && !opts.seedRejectedFor?.(model) && rejectedSeedField(e)) {
        opts.onSeedRejected?.(model);
        yield* self.stream({ ...req, dropSeed: true });
        return;
      }
      if (!yielded && !req.dropStreamUsage && !opts.streamUsageRejectedFor?.(model) && rejectedStreamUsage(e)) {
        opts.onStreamUsageRejected?.(model);
        yield* streamWithShapeRetry(self, { ...req, dropStreamUsage: true });
        return;
      }
      if (yielded || req.mergeSystems || opts.mergeSystemsFor?.(model) || !rejectedSystemPlacement(e)) throw e;
      opts.onSystemsRejected?.(model);
      yield* self.stream({ ...req, mergeSystems: true });
    }
  };

  const inner = {
    config,
    async *stream(req: CompletionRequest): AsyncIterable<StreamChunk> {
      const { url, headers, body, injections } = await buildRequest(req, true);
      // Record the exact exchange for the debug inspector. `recording` tees the raw response
      // stream into the exchange before it reaches the SSE parser, so we keep the literal bytes.
      // The real request below uses the true headers; only the RECORDED copy has its secrets masked
      // so a shared debug log can't leak the API key.
      const exchange = beginExchange({ ...req, injections }, url, headers, body);
      const dec = new TextDecoder();
      const redact = (s: string, kind: "response" | "error" = "response") => (opts.redactRecorded ? opts.redactRecorded(s, kind) : s);
      const recording = async function* (src: AsyncIterable<string | Uint8Array>) {
        try {
          for await (const chunk of src) {
            exchange.response += redact(typeof chunk === "string" ? chunk : dec.decode(chunk, { stream: true }));
            yield chunk;
          }
        } finally {
          const tail = dec.decode(); // flush a multibyte char split across the final chunk boundary
          if (tail) exchange.response += tail;
          // Once more over the whole: a match split across two chunks slipped past the per-chunk pass.
          if (opts.redactRecorded) exchange.response = redact(exchange.response);
        }
      };
      // Fold reasoning that arrives in its own field into the text stream, wrapped in the app's markers,
      // so downstream code sees one representation whether a model emits <think> inline or the gateway
      // streams `reasoning_content`. The block is opened on the first reasoning delta and closed as soon
      // as real content starts (or at end-of-stream, if the reply was reasoning only).
      // Snapshot the markers NOW: `reasoningMarkers` is a module global (last-writer-wins), so if another
      // chat starts streaming on a differently-configured provider mid-stream it must not swap ours.
      const marks = reasoningMarkers;
      let inReasoning = false;
      let reasoningDone = false; // set once content has started (or the stream ended) — no reasoning after
      const closeReasoning = (): string => {
        // Mark done even when no block was open: content-first means any later reasoning delta is dropped
        // (previously reasoningDone was only set when a block was actually closed, so content → late
        // reasoning opened a <think> mid-answer).
        reasoningDone = true;
        if (!inReasoning) return "";
        inReasoning = false;
        return marks.suffix;
      };
      // One retry with the reasoning fields dropped, when the endpoint rejected them (see
      // rejectedReasoningField). Safe here because that rejection is an HTTP error on the request itself —
      // no bytes have streamed — which `sent` enforces rather than assumes.
      const attempt = async function* (): AsyncIterable<string | Uint8Array> {
        let sent = false;
        try {
          for await (const b of transport({ url, headers, body, signal: req.signal, idleMs: idleTimeoutMs(config) })) { sent = true; yield b; }
        } catch (e) {
          if (sent || !rejectedReasoningField(e)) throw e;
          const fix = await reasoningFallback(req, e, true);
          exchange.body = fix.body; // the inspector should show what was actually accepted
          exchange.response = "";
          yield* transport({ url: fix.url, headers: fix.headers, body: fix.body, signal: req.signal, idleMs: idleTimeoutMs(config) });
        }
      };
      try {
        for await (const c of parseSSE(recording(attempt()))) {
          // Interleaved reasoning after content would open a SECOND <think> block (mis-rendered
          // downstream). Once the block has closed, drop any late reasoning deltas — a well-behaved
          // model front-loads reasoning, so this only affects rare misbehaving interleaving.
          if (c.reasoning && !reasoningDone) {
            const text = (inReasoning ? "" : marks.prefix) + c.reasoning;
            inReasoning = true;
            exchange.assistantText += text;
            yield { delta: text, done: false };
          }
          if (c.delta) {
            const text = closeReasoning() + c.delta;
            exchange.assistantText += text;
            yield { ...c, delta: text };
          }
          if (c.done) {
            const tail = closeReasoning();
            if (tail) { exchange.assistantText += tail; yield { delta: tail, done: false }; }
            exchange.done = true;
            yield c;
            break;
          }
        }
      } catch (e) {
        exchange.error = redact(String(e), "error");
        throw e;
      } finally {
        // Close a still-open reasoning marker so the recorded text isn't a dangling <think> with no
        // </think> (renders as a never-ending Thinking block). Done in `finally`, not `catch`, so it also
        // covers the consumer BREAKING the loop (generator .return() runs finally but skips catch). Normal
        // completion already closed it via closeReasoning() at c.done, so `inReasoning` is false here then.
        if (inReasoning && !reasoningDone) { inReasoning = false; exchange.assistantText += marks.suffix; }
        // Fires exactly once when the stream settles — done, error, or early break — exchange fully populated.
        notifyExchangeSinks(exchange);
      }
    },
    async complete(req: CompletionRequest): Promise<string> {
      const { url, headers, body, injections } = await buildRequest(req, false);
      const exchange = beginExchange({ ...req, injections }, url, headers, body);
      // Single request/response — no transport/SSE. An overall idle cap (folded with the caller's abort)
      // guards against a provider that connects then hangs.
      const ctrl = new AbortController();
      const onAbort = () => ctrl.abort(req.signal?.reason);
      if (req.signal) {
        if (req.signal.aborted) ctrl.abort(req.signal.reason);
        else req.signal.addEventListener("abort", onAbort, { once: true });
      }
      const own = idleTimeoutMs(config);
      const limit = own === 0 ? 0 : Math.max(own, opts.completeTimeoutMs ?? 0);
      const timer = limit
        ? setTimeout(() => ctrl.abort(new Error(`The model took too long to answer (over ${limit / 1000}s). Try again, use a faster model for this, or raise the provider's Response timeout in Settings.`)), limit)
        : undefined;
      try {
        const post = async (r: { url: string; headers: Record<string, string>; body: Record<string, unknown> }) => {
          const res = await httpFetch(r.url, { method: "POST", headers: r.headers, body: JSON.stringify(r.body), signal: ctrl.signal });
          const text = await res.text().catch(() => "");
          exchange.response = opts.redactRecorded ? opts.redactRecorded(text, "response") : text;
          if (!res.ok) throw inferenceErrorFor(res.status, text, res.headers.get("retry-after"));
          return text;
        };
        let raw: string;
        try {
          raw = await post({ url, headers, body });
        } catch (e) {
          // Same one-shot retry as `stream` — these utility calls are exactly the ones that send an effort
          // the user never chose, so a model that refuses the field must not break the feature.
          if (rejectedReasoningField(e)) {
            const fix = await reasoningFallback(req, e, false);
            exchange.body = fix.body;
            raw = await post(fix);
          } else if (rejectedSystemPlacement(e) && !req.mergeSystems && !opts.mergeSystemsFor?.(req.model ?? config.model ?? "")) {
            // The template refused the message SHAPE (multiple system messages) — merge and retry once,
            // remembering the answer per model. See streamWithShapeRetry.
            opts.onSystemsRejected?.(req.model ?? config.model ?? "");
            const fix = await buildRequest({ ...req, mergeSystems: true }, false);
            exchange.body = fix.body;
            raw = await post(fix);
          } else if (rejectedSeedField(e) && !req.dropSeed && !opts.seedRejectedFor?.(req.model ?? config.model ?? "")) {
            // The endpoint refused the seed — drop it and retry once, remembering it per (endpoint, model).
            opts.onSeedRejected?.(req.model ?? config.model ?? "");
            const fix = await buildRequest({ ...req, dropSeed: true }, false);
            exchange.body = fix.body;
            raw = await post(fix);
          } else throw e;
        }
        type Reply = { choices?: Array<{ finish_reason?: unknown; message?: { content?: unknown; reasoning_content?: unknown; reasoning?: unknown; thinking?: unknown } }>; error?: unknown };
        const parse = (s: string): Reply => {
          try { return JSON.parse(s); } catch { throw new Error("The model returned a non-JSON response."); }
        };
        let json = parse(raw);
        // Cut off having written nothing: a model that thinks anyway, whatever it was asked, spent a budget
        // sized for the answer alone (DeepSeek V4 Pro through NanoGPT on a 300-token image prompt). Utility
        // calls would read that as an empty answer, so give it room once, then say what happened.
        const spent = (j: Reply) => j?.choices?.[0]?.finish_reason === "length" && !String(j.choices[0].message?.content ?? "").trim();
        if (req.maxTokens && spent(json)) {
          const fix = await buildRequest({ ...req, maxTokens: req.maxTokens + 1024 }, false);
          exchange.body = fix.body;
          json = parse(await post(fix));
          if (spent(json)) throw new Error("The model spent its whole reply budget thinking and wrote nothing. Try again, or use a model that doesn't think for this.");
        }
        // A 200 carrying {"error": …} and no choices (several gateways do this) is an error, not an empty
        // reply — `stream` already throws for the same frame; utility calls were reporting "returned nothing".
        if (json?.error && !json?.choices?.length) {
          const err = json.error as { message?: unknown } | string;
          throw errorFrame(json.error, typeof err === "string" ? err : (typeof err?.message === "string" ? err.message : "The model returned an error."));
        }
        const msg = json?.choices?.[0]?.message ?? {};
        const marks = reasoningMarkers;
        let text = "";
        // Fold separate native reasoning into the text (wrapped in the app's markers), unless the caller excluded
        // it. Gateways spell the field differently (reasoning_content / reasoning / thinking) and it may be a
        // string or a {text}/{content} object — match the streaming path (sse.ts) so a reasoning-only reply (e.g.
        // a model that routes a whole "plan this" answer into reasoning) isn't lost on the non-streaming path.
        const rawReason = (msg.reasoning_content ?? msg.reasoning ?? msg.thinking) as unknown;
        const reason = typeof rawReason === "string"
          ? rawReason
          : ((rawReason as { text?: string; content?: string } | null)?.text ?? (rawReason as { text?: string; content?: string } | null)?.content ?? "");
        if (!req.reasoningExclude && reason.trim()) {
          text += marks.prefix + reason + marks.suffix;
        }
        text += typeof msg.content === "string" ? msg.content : "";
        exchange.assistantText = text;
        exchange.done = true;
        return text;
      } catch (e) {
        exchange.error = opts.redactRecorded ? opts.redactRecorded(String(e), "error") : String(e);
        throw e;
      } finally {
        clearTimeout(timer);
        if (req.signal) req.signal.removeEventListener("abort", onAbort);
        notifyExchangeSinks(exchange);
      }
    },
  };
  return { ...inner, stream: (req: CompletionRequest) => streamWithShapeRetry(inner, req) };
}
